// Gera automaticamente um PDF com cabeçalho da OAB/MS, texto padrão (nome do
// requerente + nº do requerimento) e tudo que a pessoa preencheu nos formulários
// do pedido (agrupado por Etapa/Seção, igual à ordem em que aparece no form) e
// devolve como File pra ser anexado junto com os demais documentos. Aplicado a
// TODOS os pedidos (config.steps já existe em qualquer um deles) — não precisa
// de nenhuma configuração extra no config.js de cada pedido.
import { jsPDF } from 'jspdf';
import { addressService } from '../../../address/services/addressService';
import LOGO_DOCUMENTO from '../../../../assets/images/logo-oabms-documento.png';
import { isCampoVisivel } from '../utils/validation';

// Dimensões originais de logo-oabms-documento.png, pra manter a proporção no PDF
const LOGO_LARGURA_PX = 608;
const LOGO_ALTURA_PX = 310;

// Paleta sóbria do documento (RGB): azul institucional (tom do globo da logo) + cinzas
const COR_AZUL = [31, 49, 84];
const COR_TEXTO = [33, 37, 41];
const COR_TEXTO_SECUNDARIO = [95, 102, 112];
const COR_FILETE = [218, 223, 230];
const COR_FAIXA = [238, 242, 247];

export { RESUMO_REQUERIMENTO_DOC_ID, RESUMO_REQUERIMENTO_TIPO_DOCUMENTO_PROCESSO } from './resumoRequerimentoConstants';

const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim() === '';
};

const formatDate = (value) => {
  const partes = String(value).split('-');
  if (partes.length !== 3) return value;
  const [ano, mes, dia] = partes;
  return `${dia}/${mes}/${ano}`;
};

const resolverValorExibicao = (field, formData, paisesPorId) => {
  const value = formData[field.name];

  switch (field.type) {
    case 'select':
    case 'radio':
      return field.options?.find((opt) => opt.value === value)?.label || value;
    case 'multiSelect':
      return (Array.isArray(value) ? value : [])
        .map((v) => field.options?.find((opt) => opt.value === v)?.label || v)
        .join(', ');
    case 'checkbox':
      return value ? 'Sim' : 'Não';
    case 'municipioBusca':
      return formData[`${field.name}Label`] || value;
    case 'paisSelect':
      return paisesPorId.get(String(value))?.nome || value;
    case 'date':
    case 'dataEmissaoCNH':
      return formatDate(value);
    default:
      return value;
  }
};

const construirLinhasPorStep = (steps, formData, paisesPorId) => {
  const stepsComConteudo = [];

  steps.forEach((step) => {
    const sections = [];

    (step.sections || []).forEach((section) => {
      const linhas = (section.fields || [])
        .filter((field) => isCampoVisivel(field, formData) && !isEmpty(formData[field.name]))
        .map((field) => ({
          // Alguns labels já terminam com ":" (ex: "Este Distrato de Sociedade é para:")
          label: String(field.label || '').replace(/:\s*$/, ''),
          tipo: field.type,
          valor: resolverValorExibicao(field, formData, paisesPorId)
        }))
        .filter((linha) => !isEmpty(linha.valor));

      if (linhas.length > 0) {
        sections.push({ title: section.title, linhas });
      }
    });

    if (sections.length > 0) {
      stepsComConteudo.push({ title: step.title, sections });
    }
  });

  return stepsComConteudo;
};

const slugify = (texto) => texto
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

// Logo carregada via URL do bundle (import de imagem do CRA) e convertida em
// dataURL pro jsPDF. Best-effort: se falhar, o PDF sai só com o texto do cabeçalho.
const carregarLogoDataUrl = async () => {
  try {
    const response = await fetch(LOGO_DOCUMENTO);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const formatarDataPorExtenso = (data) =>
  data.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * @param {Object} params
 * @param {Object} params.config - config.js do pedido
 * @param {Object} params.formData - dados preenchidos no formulário
 * @param {string} [params.nomeRequerente] - nome de quem fez o pedido (usuário logado)
 * @param {string|number} [params.numeroPedido] - nº do pedido: SolicitacaoServico.Protocolo (`protocoloNumero` do /criar), o mesmo "Nº do Pedido" de Minhas Solicitações
 * @param {boolean} [params.comTaxa] - pedido com taxa ainda não tem protocolo (só requerimento), então
 *   o acompanhamento é em "Minhas Solicitações" em vez de "Consulta de Protocolos"
 */
export async function gerarResumoRequerimentoPDF({ config, formData, nomeRequerente, numeroPedido, comTaxa = false }) {
  let paisesPorId = new Map();
  try {
    const { data } = await addressService.getCountries();
    paisesPorId = new Map((data || []).map((pais) => [String(pais.id), pais]));
  } catch {
    // Best-effort: se a API de países falhar, o campo paisSelect (se houver)
    // cai no valor bruto (ID) no PDF, sem travar a geração do resumo.
  }

  const stepsComConteudo = construirLinhasPorStep(config.steps || [], formData, paisesPorId);
  const logoDataUrl = await carregarLogoDataUrl();

  const doc = new jsPDF({ compress: true });
  const marginX = 20;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const centroX = pageWidth / 2;
  const maxLarguraTexto = pageWidth - marginX * 2;
  const margemInferior = 24; // espaço reservado pro rodapé
  const larguraColunaCampo = 62; // coluna do nome do campo na tabela de dados
  const larguraColunaValor = maxLarguraTexto - larguraColunaCampo - 4;
  const alturaLinhaTabela = 5;
  // entrelinha que dá exatamente alturaLinhaTabela (mm) em fonte 10,5pt (1pt = 0,3528mm)
  const fatorEntrelinhaTabela = alturaLinhaTabela / (10.5 * 0.3528);
  let y = 15;

  const usarCor = (tipo, [r, g, b]) => {
    if (tipo === 'texto') doc.setTextColor(r, g, b);
    if (tipo === 'linha') doc.setDrawColor(r, g, b);
    if (tipo === 'preenchimento') doc.setFillColor(r, g, b);
  };

  const quebrarLinhaSeNecessario = (alturaLinha = 7) => {
    if (y + alturaLinha > pageHeight - margemInferior) {
      doc.addPage();
      y = 20;
    }
  };

  // 1. Cabeçalho: logo + identificação da OAB/MS, centralizados, com um filete embaixo
  if (logoDataUrl) {
    try {
      const larguraLogo = 38;
      const alturaLogo = larguraLogo * (LOGO_ALTURA_PX / LOGO_LARGURA_PX);
      doc.addImage(logoDataUrl, 'PNG', centroX - larguraLogo / 2, y, larguraLogo, alturaLogo, undefined, 'FAST');
      y += alturaLogo + 7;
    } catch {
      // imagem inválida: segue só com o texto do cabeçalho
    }
  }

  usarCor('texto', COR_AZUL);
  doc.setFont('times', 'bold');
  doc.setFontSize(13);
  doc.text('ORDEM DOS ADVOGADOS DO BRASIL', centroX, y, { align: 'center' });
  y += 5.5;
  usarCor('texto', COR_TEXTO_SECUNDARIO);
  doc.setFont('times', 'normal');
  doc.setFontSize(10.5);
  doc.text('SEÇÃO DE MATO GROSSO DO SUL', centroX, y, { align: 'center' });
  y += 5;

  usarCor('linha', COR_AZUL);
  doc.setLineWidth(0.6);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 16;

  // 2. Texto padrão introdutório
  usarCor('texto', COR_TEXTO);
  doc.setFont('times', 'bold');
  doc.setFontSize(12);
  const linhasSaudacao = doc.splitTextToSize(`Senhor(a): ${nomeRequerente || '—'}`, maxLarguraTexto);
  doc.text(linhasSaudacao, marginX, y);
  y += linhasSaudacao.length * 6 + 6;

  doc.setFont('times', 'normal');
  const opcaoAcompanhamento = comTaxa ? 'Minhas Solicitações' : 'Consulta de Protocolos';
  const textoPadrao = `Seu pedido registrado sob o nº ${numeroPedido || '—'}, informa que é necessário o acesso à opção "${opcaoAcompanhamento}" para acompanhar o andamento.`;
  const linhasTextoPadrao = doc.splitTextToSize(textoPadrao, maxLarguraTexto);
  doc.text(linhasTextoPadrao, marginX, y, { align: 'justify', maxWidth: maxLarguraTexto, lineHeightFactor: 1.4 });
  y += linhasTextoPadrao.length * 6 + 12;

  // 3. Dados preenchidos no formulário
  quebrarLinhaSeNecessario(20);
  usarCor('texto', COR_TEXTO_SECUNDARIO);
  doc.setFont('times', 'normal');
  doc.setFontSize(9);
  doc.text('DADOS DO REQUERIMENTO', centroX, y, { align: 'center', charSpace: 0.6 });
  y += 6;
  usarCor('texto', COR_AZUL);
  doc.setFont('times', 'bold');
  doc.setFontSize(13);
  const linhasTitulo = doc.splitTextToSize(config.nome || 'Resumo do Requerimento', maxLarguraTexto);
  doc.text(linhasTitulo, centroX, y, { align: 'center' });
  y += linhasTitulo.length * 6 + 6;

  stepsComConteudo.forEach((step) => {
    // Faixa discreta com o nome da etapa
    quebrarLinhaSeNecessario(22);
    usarCor('preenchimento', COR_FAIXA);
    doc.rect(marginX, y - 5, maxLarguraTexto, 7.5, 'F');
    usarCor('preenchimento', COR_AZUL);
    doc.rect(marginX, y - 5, 1.2, 7.5, 'F');
    usarCor('texto', COR_AZUL);
    doc.setFont('times', 'bold');
    doc.setFontSize(10.5);
    doc.text(step.title.toUpperCase(), marginX + 4, y);
    y += 9;

    step.sections.forEach((section) => {
      quebrarLinhaSeNecessario(14);
      usarCor('texto', COR_TEXTO);
      doc.setFont('times', 'bold');
      doc.setFontSize(11);
      doc.text(section.title, marginX, y);
      y += 2.5;

      // Tabela de 2 colunas: nome do campo | valor, com filete entre as linhas
      doc.setFontSize(10.5);
      doc.setLineWidth(0.2);
      section.linhas.forEach(({ label, valor, tipo }) => {
        // Checkbox (declarações/termos, geralmente texto longo) ocupa a largura toda,
        // marcado como em formulário oficial: "( X ) DECLARO que..."
        if (tipo === 'checkbox') {
          const marcador = valor === 'Sim' ? '( X )' : '(    )';
          const recuo = 11;
          const linhasDeclaracao = doc.splitTextToSize(label, maxLarguraTexto - recuo - 1);
          const alturaLinha = linhasDeclaracao.length * alturaLinhaTabela + 3;

          quebrarLinhaSeNecessario(alturaLinha);
          const topo = y;
          usarCor('texto', COR_TEXTO);
          doc.setFont('times', 'bold');
          doc.text(marcador, marginX + 1, topo + 5);
          doc.setFont('times', 'normal');
          doc.text(linhasDeclaracao, marginX + recuo, topo + 5, { align: 'justify', maxWidth: maxLarguraTexto - recuo - 1, lineHeightFactor: fatorEntrelinhaTabela });

          y = topo + alturaLinha;
          usarCor('linha', COR_FILETE);
          doc.line(marginX, y, pageWidth - marginX, y);
          return;
        }

        const linhasCampo = doc.splitTextToSize(label, larguraColunaCampo);
        const linhasValor = doc.splitTextToSize(String(valor), larguraColunaValor);
        const alturaLinha = Math.max(linhasCampo.length, linhasValor.length) * alturaLinhaTabela + 3;

        quebrarLinhaSeNecessario(alturaLinha);
        const topo = y;
        usarCor('texto', COR_TEXTO_SECUNDARIO);
        doc.setFont('times', 'normal');
        doc.text(linhasCampo, marginX + 1, topo + 5, { lineHeightFactor: fatorEntrelinhaTabela });
        usarCor('texto', COR_TEXTO);
        doc.text(linhasValor, marginX + larguraColunaCampo + 4, topo + 5, { lineHeightFactor: fatorEntrelinhaTabela });

        y = topo + alturaLinha;
        usarCor('linha', COR_FILETE);
        doc.line(marginX, y, pageWidth - marginX, y);
      });
      y += 7;
    });
    y += 2;
  });

  // Local e data
  y += 4;
  quebrarLinhaSeNecessario(8);
  usarCor('texto', COR_TEXTO);
  doc.setFont('times', 'normal');
  doc.setFontSize(12);
  doc.text(`Campo Grande, ${formatarDataPorExtenso(new Date())}.`, marginX, y);

  // Rodapé em todas as páginas: filete + identificação à esquerda + paginação à direita
  const totalPaginas = doc.internal.getNumberOfPages();
  const geradoEm = new Date().toLocaleString('pt-BR');
  const referencia = numeroPedido ? `Pedido nº ${numeroPedido} · ` : '';
  for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
    doc.setPage(pagina);
    usarCor('linha', COR_FILETE);
    doc.setLineWidth(0.3);
    doc.line(marginX, pageHeight - 15, pageWidth - marginX, pageHeight - 15);
    usarCor('texto', COR_TEXTO_SECUNDARIO);
    doc.setFont('times', 'normal');
    doc.setFontSize(8);
    doc.text('OAB/MS · Ordem dos Advogados do Brasil, Seção de Mato Grosso do Sul', marginX, pageHeight - 10.5);
    doc.text(`Página ${pagina} de ${totalPaginas}`, pageWidth - marginX, pageHeight - 10.5, { align: 'right' });
    doc.setFont('times', 'italic');
    doc.text(`${referencia}Documento gerado automaticamente em ${geradoEm}`, marginX, pageHeight - 6.5);
  }

  const nomeArquivo = `resumo-${slugify(config.nome || config.id || 'requerimento')}.pdf`;
  const blob = doc.output('blob');
  return new File([blob], nomeArquivo, { type: 'application/pdf' });
}
